import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useBrief } from '../hooks/useData.js';
import { api } from '../lib/api.js';
import { timeAgo } from '../lib/format.js';
import { Button } from './ui/Button.js';
import { Drawer } from './ui/Drawer.js';

/** §21.3 / step 17 — situation brief banner under the top bar. */
export function BriefBanner() {
  const { data } = useBrief();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const gen = useMutation({
    mutationFn: api.generateBrief,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brief'] }),
  });

  const brief = data?.brief ?? null;

  if (!brief) {
    return (
      <div className="flex items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-panel)] px-4 py-2 text-xs text-[var(--color-muted)]">
        <Sparkles size={13} className="text-[var(--color-muted)]" />
        <span>No situation brief yet.</span>
        <Button
          className="px-2 py-0.5 text-[11px]"
          disabled={gen.isPending}
          onClick={() => gen.mutate()}
        >
          {gen.isPending ? 'Generating…' : 'Generate now'}
        </Button>
        {gen.data && !gen.data.generated && (
          <span className="text-[var(--color-sev-medium)]">
            AI unavailable — set ANTHROPIC_API_KEY.
          </span>
        )}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full border-b border-[var(--color-border)] bg-[var(--color-panel)] px-5 py-4 text-left hover:bg-[var(--color-panel-2)]"
      >
        <div className="flex items-baseline gap-2">
          <Sparkles size={15} className="shrink-0 translate-y-0.5 text-[var(--color-sev-high)]" />
          <span className="text-base font-semibold">{brief.headline}</span>
          <span className="mono ml-auto shrink-0 text-[11px] text-[var(--color-muted)]">
            brief · {timeAgo(brief.generatedAt)}
          </span>
        </div>
        <p className="mt-1.5 line-clamp-3 pl-6 text-sm leading-relaxed text-[var(--color-muted)]">
          {brief.bodyMarkdown}
        </p>
        <span className="mt-1 block pl-6 text-[11px] text-[var(--color-muted)]">
          Click to read the full brief →
        </span>
      </button>

      <Drawer open={open} onClose={() => setOpen(false)} title="Situation brief">
        <div className="space-y-4">
          <h2 className="text-base font-semibold">{brief.headline}</h2>
          <p className="whitespace-pre-wrap text-sm text-[var(--color-text)]">
            {brief.bodyMarkdown}
          </p>
          <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)] p-3 text-xs text-[var(--color-muted)]">
            <div>
              Generated {timeAgo(brief.generatedAt)} from {brief.inputEventIds.length} matched
              event(s).
            </div>
            <div className="mono mt-1">
              {brief.model}
              {brief.costUsd != null ? ` · $${brief.costUsd.toFixed(5)}` : ''}
              {brief.cacheHit ? ' · cached' : ''}
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
}
